import React, { useRef, useEffect } from 'react';
import 'emoji-picker-element';

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
  isOpen: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement | null>;
}

const EmojiPicker: React.FC<EmojiPickerProps> = ({ 
  onEmojiSelect, 
  isOpen, 
  onClose, 
  triggerRef 
}) => {
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current && 
        !pickerRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  useEffect(() => {
    const pickerContainer = pickerRef.current;
    if (isOpen && pickerContainer && triggerRef.current) {
      // Clear any existing picker
      pickerContainer.innerHTML = '';
      
      // Create the emoji picker element
      const emojiPicker = document.createElement('emoji-picker');
      
      // Add event listener for emoji selection
      const handleEmojiClick = (event: any) => {
        onEmojiSelect(event.detail.unicode);
        onClose();
      };

      emojiPicker.addEventListener('emoji-click', handleEmojiClick);
      pickerContainer.appendChild(emojiPicker);

      // Position the picker relative to the trigger button
      const updatePosition = () => {
        if (triggerRef.current && pickerContainer) {
          const triggerRect = triggerRef.current.getBoundingClientRect();
          const pickerWidth = 352;
          const pickerHeight = 435;
          const margin = 8;
          
          let top = triggerRect.top - pickerHeight - margin;
          let left = triggerRect.left + (triggerRect.width / 2) - (pickerWidth / 2);
          const viewportWidth = window.innerWidth;
          
          if (left < margin) left = margin;
          else if (left + pickerWidth > viewportWidth - margin) left = viewportWidth - pickerWidth - margin;
          if (top < margin) top = triggerRect.bottom + margin;
          
          pickerContainer.style.top = `${top}px`;
          pickerContainer.style.left = `${left}px`;
        }
      };

      updatePosition();

      const handleScroll = () => updatePosition();
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', handleScroll, { passive: true });

      return () => {
        emojiPicker.removeEventListener('emoji-click', handleEmojiClick);
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
        if (pickerContainer && pickerContainer.contains(emojiPicker)) {
          pickerContainer.removeChild(emojiPicker);
        }
      };
    }
  }, [isOpen, onEmojiSelect, onClose, triggerRef]);

  if (!isOpen) return null;

  return (
    <div
      ref={pickerRef}
      className="fixed z-50"
      style={{ 
        width: '352px', // Standard emoji picker width
        height: '435px' // Standard emoji picker height
      }}
    />
  );
};

export default EmojiPicker;